import type { ViewConfig } from "./reportsV2";

/**
 * Types for the reports microservice.
 *
 * The frontend treats every report as schema-driven: columns, filters,
 * default date range and friendly labels all come back from
 * GET /report-definitions, so adding a new built-in report means zero
 * frontend code changes.
 */

export type ReportColumnType =
  | "string"
  | "int"
  /** Fractional numeric (e.g. package units sold) — up to 2 decimals. */
  | "decimal"
  | "currency"
  | "date"
  | "datetime";

export interface ReportColumn {
  key: string;
  label: string;
  type: ReportColumnType;
  is_grouping: boolean;
  description?: string | null;
  /**
   * When true, the column is hidden by default and only rendered once
   * the operator opts in via the Columns menu — the choice persists in
   * `SalonReport.shown_columns`, not `hidden_columns`. Used by the
   * Transaction Log payment-method breakdown columns (including the
   * per-salon Off-Platform Payment columns surfaced dynamically at run
   * time). Normal columns leave this false and stay governed by
   * `hidden_columns`.
   */
  default_hidden?: boolean;
  /**
   * When true, this is a contra/refund column: the backend sends a positive
   * magnitude, but it represents money going OUT, so it renders in accounting
   * parentheses — ($123.45) — on screen and exports as a real negative
   * (-123.45). Optional for backward compatibility: a backend not yet
   * returning it (or a non-refund column) simply omits it, so reads treat a
   * missing value as false.
   */
  accounting_negative?: boolean;
}

export type ReportFilterKind =
  | "staff_multiselect"
  | "service_multiselect"
  | "product_multiselect"
  | "enum_multiselect"
  | "enum_select"
  | "service_or_product_multiselect"
  | "string";

export interface ReportFilterOption {
  value: string;
  label: string;
}

export interface ReportFilter {
  key: string;
  label: string;
  kind: ReportFilterKind;
  /**
   * Static value/label pairs supplied by the backend for kinds whose
   * options aren't loaded from a salon-scoped roster on the
   * frontend (e.g. enum_multiselect for transaction / appointment
   * statuses). Empty array for dynamic kinds.
   *
   * For enum_select (a single choice), the first option is the default —
   * what an unset filter means on the backend.
   */
  options?: ReportFilterOption[];
}

export type DateRangePreset =
  | "today"
  | "yesterday"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "last_7_days"
  | "last_14_days"
  | "last_30_days"
  | "last_year_rolling"
  | "this_year"
  | "last_year"
  | "quarter_to_date"
  | "fixed_dates";

export const DATE_RANGE_PRESETS: ReadonlyArray<{
  value: DateRangePreset;
  label: string;
}> = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_year_rolling", label: "Last year" },
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "this_month", label: "Month to date" },
  { value: "last_month", label: "Last month" },
  { value: "quarter_to_date", label: "Quarter to date" },
  { value: "this_year", label: "Year to date" },
  { value: "last_year", label: "Last year (calendar)" },
  { value: "fixed_dates", label: "Custom range" },
];

/**
 * Multi-column structure for the date range dropdown menu in the reports
 * toolbar. Each column groups conceptually related presets so users can
 * scan the menu quickly. Older presets (this_week, last_week, last_month,
 * last_year) remain valid in DATE_RANGE_PRESETS for backward compatibility
 * with saved reports but are intentionally omitted from the menu.
 */
export const DATE_RANGE_MENU_GROUPS: ReadonlyArray<{
  label: string;
  options: ReadonlyArray<{ value: DateRangePreset; label: string }>;
}> = [
  {
    label: "Relative",
    options: [
      { value: "today", label: "Today" },
      { value: "yesterday", label: "Yesterday" },
      { value: "last_7_days", label: "Last 7 days" },
      { value: "last_14_days", label: "Last 14 days" },
      { value: "last_30_days", label: "Last 30 days" },
      { value: "last_year_rolling", label: "Last year" },
    ],
  },
  {
    label: "To date",
    options: [
      { value: "this_month", label: "Month to date" },
      { value: "quarter_to_date", label: "Quarter to date" },
      { value: "this_year", label: "Year to date" },
    ],
  },
];

/**
 * Semantic icon key sent by the API for catalog entries and categories
 * (e.g. "calendar", "receipt", "history"). The portal maps it onto a Tabler
 * icon (components/reports/shell/reportIcons); unknown keys fall back to a
 * generic report icon.
 */
export type ReportIconKey = string;

/** A catalog category, in display order (only categories with ≥1 report). */
export interface ReportCategory {
  key: string;
  label: string;
  icon?: ReportIconKey | null;
}

/**
 * A per-row drill-down with its OWN columns (e.g. Staff Earnings' "Commission
 * by service"). Each parent row carries its children under `field`; the table
 * renders them as a nested table titled `label`. Absent / null for reports
 * without one (and from older backends).
 */
export interface ReportBreakdownSpec {
  field: string;
  label: string;
  columns: ReportColumn[];
}

export interface ReportDefinition {
  report_type: string;
  name: string;
  description: string;
  /**
   * Catalog placement (Reports v2 shell). Legacy definitions always report
   * "legacy". Optional: a backend that predates the shell omits all three.
   */
  category?: string;
  /** Sub-section inside the category, e.g. "Sales". */
  sub_group?: string | null;
  icon?: ReportIconKey | null;
  default_date_range: DateRangePreset;
  supports_staff_breakdown: boolean;
  /**
   * When true, parent rows carry a `line_items` array of children the
   * table expands inline. Used by the Transaction Log report so an
   * operator can drill from a transaction into its individual
   * services / products / fees.
   */
  supports_line_item_breakdown: boolean;
  supports_totals: boolean;
  /** False when the report can't be narrowed to one staff member (refused
   *  for an own-scoped caller). Absent from older backends. */
  supports_own_scope?: boolean;
  /** Per-row drill-down with its own columns (see ReportBreakdownSpec). */
  breakdown?: ReportBreakdownSpec | null;
  columns: ReportColumn[];
  filters: ReportFilter[];
}

/** A report's saved view: a v2 ViewConfig (types/reportsV2) for new-engine
 *  report types, `{}` for legacy ones. */
export type ReportViewConfig = ViewConfig | Record<string, unknown>;

export interface SortRule {
  column: string;
  direction: "asc" | "desc";
}

export interface SalonReport {
  id: string;
  salon_id: string;
  /** Location the saved report belongs to (newer API responses). */
  business_location_id?: string;
  report_type: string;
  custom_name: string | null;
  /**
   * User-editable description (max 500). Null / absent → show the catalog
   * description. Optional: older API responses omit it.
   */
  custom_description?: string | null;
  /**
   * Saved view for v2 report types (`{}` for legacy reports). The v2 canvas
   * normalizes it into a ViewConfig.
   */
  view_config?: ReportViewConfig;
  default_date_range: DateRangePreset | null;
  fixed_start_dt: string | null;
  fixed_end_dt: string | null;
  hidden_columns: string[];
  /**
   * Opt-in visibility for columns the definition marks `default_hidden`
   * (e.g. the Transaction Log payment-method breakdown columns). A
   * default_hidden column is shown only when its key appears here.
   * Optional for backward compatibility: rows persisted before this
   * field existed (and any backend not yet returning it) omit it, so
   * every read guards with `?? []` / `|| []`.
   */
  shown_columns?: string[];
  sort_config: SortRule[];
  filter_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  /** ISO timestamp of the last successful run, or null if never run. */
  last_run_at: string | null;
  /**
   * Report sharing. Optional: older API responses omit them.
   * `share_count` — copies of this report shared with others (owner's view).
   * `shared_from_report_id` / `shared_by` — set on a copy someone shared with
   * the caller; `sync_with_source` — that copy follows the original's view.
   */
  share_count?: number;
  shared_from_report_id?: string | null;
  shared_by?: { assignment_id: string; name: string | null } | null;
  sync_with_source?: boolean;
}

/** A staff member at the location, as the report-sharing endpoints name them. */
export interface ReportShareStaff {
  assignment_id: string;
  name: string;
  role: string;
}

/** One copy of a report, shared with `recipient`. */
export interface ReportShare {
  /** The recipient's copy (its salon_reports id). */
  id: string;
  recipient: ReportShareStaff;
  /** Saving the original's view overwrites this copy's view. */
  sync_with_source: boolean;
  shared_at: string;
}

/** Someone the report can be shared with; `can_receive` false when their
 *  role can't open this report. */
export interface ReportShareCandidate extends ReportShareStaff {
  can_receive: boolean;
}

/** GET …/reports/{id}/shares */
export interface ReportSharesResponse {
  shares: ReportShare[];
  candidates: ReportShareCandidate[];
}

/** POST …/reports/{id}/shares */
export interface CreateReportSharesRequest {
  recipient_assignment_ids: string[];
  sync_with_source: boolean;
}

export interface ReportRunResult {
  definition: ReportDefinition;
  report: SalonReport;
  date_range: {
    preset: DateRangePreset;
    start: string;
    end: string;
  };
  /** Each row is a flat dict keyed by column.key. Service/product sales
   *  rows additionally carry a `staff_breakdown` field for expansion;
   *  Transaction Log rows carry a `line_items` array for the same
   *  purpose. */
  rows: Array<
    Record<string, unknown> & {
      staff_breakdown?: StaffBreakdownEntry[];
      line_items?: Array<Record<string, unknown>>;
    }
  >;
}

export interface StaffBreakdownEntry {
  staff_id: string | null;
  staff_member: string;
  sales: number;
  count: number;
}

// ----------------------------------------------------------------------
// Request payloads
// ----------------------------------------------------------------------

export interface AddSalonReportRequest {
  report_type: string;
  custom_name?: string | null;
  custom_description?: string | null;
  /** Accepted for v2 report types only (the API rejects it for legacy). */
  view_config?: ReportViewConfig;
  default_date_range?: DateRangePreset | null;
  fixed_start_dt?: string | null;
  fixed_end_dt?: string | null;
  hidden_columns?: string[];
  shown_columns?: string[];
  sort_config?: SortRule[];
  filter_config?: Record<string, unknown>;
}

export interface UpdateSalonReportRequest {
  custom_name?: string | null;
  custom_description?: string | null;
  /** Accepted for v2 report types only (the API rejects it for legacy). */
  view_config?: ReportViewConfig;
  default_date_range?: DateRangePreset | null;
  fixed_start_dt?: string | null;
  fixed_end_dt?: string | null;
  hidden_columns?: string[];
  shown_columns?: string[];
  sort_config?: SortRule[];
  filter_config?: Record<string, unknown>;
}

export interface RunReportRequest {
  date_range_preset?: DateRangePreset | null;
  start_date?: string | null;
  end_date?: string | null;
  filter_overrides?: Record<string, unknown>;
  options?: {
    breakdown_by_staff?: boolean;
  };
}

/**
 * Catalog summary of a new-engine ("v2") report type, as listed by
 * GET /report-definitions → `v2_definitions`. The full definition (metrics,
 * group-bys, filters, default view) carries more fields, which the v2 report
 * canvas types separately.
 */
export interface ReportV2DefinitionSummary {
  report_type: string;
  engine: "v2";
  name: string;
  description: string;
  category: string;
  sub_group?: string | null;
  icon?: ReportIconKey | null;
  /** Hard-coded "new" flag: NEW badge + "What's new" + the "N new" pill. */
  is_new?: boolean;
  /** "table" (grouped tree, e.g. Daily Sales Report) or "ledger" (summary,
   *  e.g. Sales Summary). Absent from older backends ⇒ "table". */
  layout?: ReportLayout;
  /** The `reports` permission that opens this report (section_permissions
   *  .reports.<access_permission>). Absent from older backends ⇒ "scope". */
  access_permission?: string;
  /** False when the report only shows location-wide totals (refused for an
   *  own-scoped caller). Absent from older backends. */
  supports_own_scope?: boolean;
}

export type ReportLayout = "table" | "ledger";

export interface ReportDefinitionsResponse {
  definitions: ReportDefinition[];
  /** Display order of catalog categories (Reports v2 shell). */
  categories?: ReportCategory[];
  /** New-engine report types; absent or empty until the backend flag is on. */
  v2_definitions?: ReportV2DefinitionSummary[];
}

export interface SalonReportsResponse {
  reports: SalonReport[];
}
